import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Link, Stack, Typography } from '@mui/material';

import type { DeliveryTone } from '../vocabulary';
import { channelPhrase, deliveryStatusLabel, deliveryStatusTone } from '../vocabulary';

export interface DeliveryHistoryEntry {
  touchpointId: string;
  channel: string;
  /** The vendor's word (queued|sent|delivered|failed|…), translated on render. */
  status: string;
  /** Already formatted for reading; undefined when nothing has a timestamp. */
  timestamp?: string;
  /** Absolute ISO value, for the title attribute. */
  timestampIso?: string;
  /** The secure link lifted out of the payload, rendered as a real anchor. */
  secureLink?: string;
  /**
   * Whether this message went out as a reminder, as recorded when it was sent.
   *
   * Null or absent means the send predates that record, and the row simply says
   * nothing — the contract's own words are that null "is the honest answer
   * rather than a guess". Never derived from `sequence`: a send that reached
   * nobody spends a sequence too, so the ladder position is not a count of what
   * the patient received.
   */
  isReminder?: boolean | null;
}

export interface DeliveryHistoryListProps {
  entries: DeliveryHistoryEntry[];
}

const TONE_STYLE: Record<DeliveryTone, { color: string; bg: string }> = {
  pending: { color: ELIG_TOKENS.amber, bg: ELIG_TOKENS.amberTint },
  good: { color: ELIG_TOKENS.green, bg: ELIG_TOKENS.greenTint },
  bad: { color: ELIG_TOKENS.red, bg: ELIG_TOKENS.redTint },
};

const DeliveryStatusChip = ({ status }: { status: string }) => {
  const tone = TONE_STYLE[deliveryStatusTone(status)];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.75,
        py: 0.125,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        color: tone.color,
        backgroundColor: tone.bg,
        whiteSpace: 'nowrap',
      }}
    >
      {deliveryStatusLabel(status)}
    </Box>
  );
};

/**
 * AC-20: what was sent to this patient, in words a staff member reads —
 * channel, when, whether it arrived, and the secure link itself.
 *
 * The raw message payload is deliberately NOT rendered. It used to be, because
 * AC-20 asked for "the full payload with the secure link" and the acceptance
 * script read it; both have been revised (GDN-3398). A JSON dump in a clinical
 * surface asks staff to parse a machine's data structure to answer "did it
 * arrive" — a question the status chip now answers directly. The payload is
 * still stored server-side, so a later "View message" expansion has something
 * to open.
 */
export const DeliveryHistoryList = ({ entries }: DeliveryHistoryListProps) => {
  if (entries.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft }}>
        Nothing has been sent yet.
      </Typography>
    );
  }

  return (
    <Box
      component="ul"
      aria-label="Delivery history"
      sx={{
        listStyle: 'none',
        m: 0,
        pl: 1.5,
        borderLeft: `2px solid ${ELIG_TOKENS.line}`,
      }}
    >
      {entries.map(entry => (
        <Box component="li" key={entry.touchpointId} sx={{ py: 0.75 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: ELIG_TOKENS.ink }}
              title={entry.timestampIso}
            >
              {/* Nothing at all when the send predates the record. An
                  unlabelled row is honest; "Request" by default would assert
                  something about a message nobody recorded. */}
              {entry.isReminder === true && 'Reminder · '}
              {entry.isReminder === false && 'Request · '}
              {/* The word, not the stored enum — same rule as the row summary
                  (criterion 10). "EMAIL" shouted mid-line is the storage value
                  on screen; SMS keeps its capitals because that is the word. */}
              {channelPhrase(entry.channel)}
              {entry.timestamp ? ` · ${entry.timestamp}` : ''}
            </Typography>
            <DeliveryStatusChip status={entry.status} />
          </Stack>

          {/* One summary line. The anchor shows the URL rather than a label so
              a person — or the acceptance script — can read the slug off it. */}
          {entry.secureLink && (
            <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft }}>
              Secure link:{' '}
              <Link
                href={entry.secureLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ wordBreak: 'break-all' }}
              >
                {entry.secureLink}
              </Link>
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};
